/**
 * Pixel-art sprites. Original 8-bit designs created with simple SVG.
 * No copyrighted character art, no game sprites.
 *
 * Each sprite is a small grid of <rect> elements, scaled up via
 * `image-rendering: pixelated` to give the chunky 8-bit look.
 */
import { type CSSProperties } from 'react';

interface SpriteProps {
  size?: number;
  style?: CSSProperties;
  className?: string;
}

const baseProps = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 16 16',
  xmlns: 'http://www.w3.org/2000/svg',
  style: { imageRendering: 'pixelated' as const, shapeRendering: 'crispEdges' as const },
  className: 'gb-sprite',
});

const DARK = '#0f380f';
const MID = '#306230';
const SHADOW = '#8bac0f';
const BG = '#9bbc0f';

/** A simple chunky door icon. */
export function DoorSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      {/* frame */}
      <rect x="3" y="2" width="10" height="12" fill={DARK} />
      <rect x="4" y="3" width="8" height="10" fill={MID} />
      {/* door panel */}
      <rect x="5" y="4" width="6" height="8" fill={SHADOW} />
      <rect x="5" y="4" width="6" height="1" fill={DARK} />
      <rect x="5" y="11" width="6" height="1" fill={DARK} />
      {/* handle */}
      <rect x="9" y="7" width="1" height="2" fill={DARK} />
      <rect x="10" y="7" width="1" height="1" fill={DARK} />
      {/* floor */}
      <rect x="2" y="14" width="12" height="1" fill={DARK} />
    </svg>
  );
}

/** Door with arrow showing it's opening. */
export function DoorOpenSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="3" y="2" width="10" height="12" fill={DARK} />
      <rect x="4" y="3" width="8" height="10" fill={MID} />
      {/* open door rotated */}
      <rect x="5" y="3" width="2" height="10" fill={SHADOW} />
      <rect x="5" y="3" width="2" height="1" fill={DARK} />
      <rect x="5" y="12" width="2" height="1" fill={DARK} />
      {/* arrow indicating opening */}
      <rect x="9" y="7" width="4" height="1" fill={DARK} />
      <rect x="12" y="6" width="1" height="3" fill={DARK} />
      <rect x="13" y="5" width="1" height="5" fill={DARK} />
      <rect x="2" y="14" width="12" height="1" fill={DARK} />
    </svg>
  );
}

/** Doorbell bell icon. */
export function BellSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      {/* bell body */}
      <rect x="5" y="3" width="6" height="2" fill={DARK} />
      <rect x="4" y="5" width="8" height="6" fill={DARK} />
      <rect x="4" y="5" width="8" height="1" fill={MID} />
      <rect x="5" y="6" width="6" height="4" fill={SHADOW} />
      <rect x="5" y="10" width="6" height="1" fill={DARK} />
      {/* clapper */}
      <rect x="7" y="11" width="2" height="2" fill={DARK} />
      <rect x="6" y="13" width="4" height="1" fill={DARK} />
      {/* handle on top */}
      <rect x="7" y="2" width="2" height="1" fill={DARK} />
    </svg>
  );
}

/** Megaphone for announcements. */
export function MegaphoneSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="2" y="5" width="8" height="6" fill={DARK} />
      <rect x="3" y="6" width="6" height="4" fill={SHADOW} />
      <rect x="3" y="6" width="6" height="1" fill={MID} />
      <rect x="10" y="3" width="2" height="10" fill={DARK} />
      <rect x="12" y="5" width="1" height="6" fill={DARK} />
      {/* handle */}
      <rect x="4" y="11" width="2" height="3" fill={DARK} />
      {/* sound waves */}
      <rect x="14" y="6" width="1" height="4" fill={DARK} />
      <rect x="15" y="5" width="1" height="6" fill={DARK} />
    </svg>
  );
}

/** Wrench for incidents. */
export function WrenchSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="3" y="3" width="6" height="3" fill={DARK} />
      <rect x="2" y="4" width="1" height="2" fill={DARK} />
      <rect x="9" y="3" width="1" height="2" fill={DARK} />
      <rect x="4" y="4" width="4" height="1" fill={SHADOW} />
      <rect x="3" y="9" width="9" height="3" fill={DARK} transform="rotate(45 7.5 10.5)" />
      <rect x="4" y="10" width="7" height="1" fill={SHADOW} transform="rotate(45 7.5 10.5)" />
    </svg>
  );
}

/** Ballot/vote box. */
export function BallotSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="3" y="2" width="10" height="3" fill={DARK} />
      <rect x="3" y="2" width="10" height="1" fill={MID} />
      <rect x="3" y="5" width="10" height="9" fill={DARK} />
      <rect x="4" y="6" width="8" height="7" fill={SHADOW} />
      <rect x="4" y="6" width="8" height="1" fill={DARK} />
      {/* ballot slot */}
      <rect x="5" y="8" width="6" height="1" fill={DARK} />
      {/* check mark inside */}
      <rect x="6" y="10" width="1" height="1" fill={DARK} />
      <rect x="7" y="11" width="1" height="1" fill={DARK} />
      <rect x="8" y="10" width="1" height="1" fill={DARK} />
      <rect x="9" y="9" width="1" height="1" fill={DARK} />
      <rect x="10" y="8" width="1" height="1" fill={DARK} />
    </svg>
  );
}

/** Coin / expensa. */
export function CoinSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="5" y="2" width="6" height="1" fill={DARK} />
      <rect x="3" y="3" width="10" height="1" fill={DARK} />
      <rect x="2" y="4" width="12" height="8" fill={DARK} />
      <rect x="3" y="5" width="10" height="6" fill={SHADOW} />
      <rect x="3" y="5" width="10" height="1" fill={MID} />
      <rect x="2" y="12" width="12" height="1" fill={DARK} />
      <rect x="3" y="13" width="10" height="1" fill={DARK} />
      <rect x="5" y="14" width="6" height="1" fill={DARK} />
      {/* V mark in middle */}
      <rect x="6" y="6" width="1" height="3" fill={DARK} />
      <rect x="7" y="8" width="1" height="1" fill={DARK} />
      <rect x="8" y="8" width="1" height="1" fill={DARK} />
      <rect x="9" y="6" width="1" height="3" fill={DARK} />
      <rect x="7" y="9" width="2" height="1" fill={DARK} />
    </svg>
  );
}

/** User profile / trainer card. */
export function UserSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      {/* head */}
      <rect x="6" y="2" width="4" height="1" fill={DARK} />
      <rect x="5" y="3" width="6" height="4" fill={DARK} />
      <rect x="6" y="3" width="4" height="3" fill={SHADOW} />
      <rect x="5" y="7" width="6" height="1" fill={DARK} />
      {/* body */}
      <rect x="3" y="8" width="10" height="5" fill={DARK} />
      <rect x="4" y="9" width="8" height="3" fill={SHADOW} />
      <rect x="4" y="12" width="8" height="1" fill={DARK} />
      {/* legs */}
      <rect x="5" y="13" width="2" height="2" fill={DARK} />
      <rect x="9" y="13" width="2" height="2" fill={DARK} />
    </svg>
  );
}

/** Gear for settings. */
export function GearSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      {/* outer teeth */}
      <rect x="7" y="1" width="2" height="2" fill={DARK} />
      <rect x="7" y="13" width="2" height="2" fill={DARK} />
      <rect x="1" y="7" width="2" height="2" fill={DARK} />
      <rect x="13" y="7" width="2" height="2" fill={DARK} />
      <rect x="3" y="3" width="2" height="2" fill={DARK} />
      <rect x="11" y="3" width="2" height="2" fill={DARK} />
      <rect x="3" y="11" width="2" height="2" fill={DARK} />
      <rect x="11" y="11" width="2" height="2" fill={DARK} />
      {/* core */}
      <rect x="4" y="4" width="8" height="8" fill={DARK} />
      <rect x="5" y="5" width="6" height="6" fill={SHADOW} />
      <rect x="6" y="6" width="4" height="4" fill={BG} />
    </svg>
  );
}

/** QR / share. */
export function QRSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="2" y="2" width="5" height="5" fill={DARK} />
      <rect x="3" y="3" width="3" height="3" fill={SHADOW} />
      <rect x="9" y="2" width="5" height="5" fill={DARK} />
      <rect x="10" y="3" width="3" height="3" fill={SHADOW} />
      <rect x="2" y="9" width="5" height="5" fill={DARK} />
      <rect x="3" y="10" width="3" height="3" fill={SHADOW} />
      <rect x="9" y="9" width="2" height="2" fill={DARK} />
      <rect x="12" y="9" width="2" height="2" fill={DARK} />
      <rect x="9" y="12" width="2" height="2" fill={DARK} />
      <rect x="12" y="12" width="2" height="2" fill={DARK} />
    </svg>
  );
}

/** Heart / HP. */
export function HeartSprite({ size = 16, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="3" y="3" width="3" height="2" fill={DARK} />
      <rect x="10" y="3" width="3" height="2" fill={DARK} />
      <rect x="2" y="4" width="1" height="1" fill={DARK} />
      <rect x="13" y="4" width="1" height="1" fill={DARK} />
      <rect x="2" y="5" width="1" height="5" fill={DARK} />
      <rect x="13" y="5" width="1" height="5" fill={DARK} />
      <rect x="3" y="5" width="10" height="1" fill={DARK} />
      <rect x="3" y="9" width="2" height="1" fill={DARK} />
      <rect x="11" y="9" width="2" height="1" fill={DARK} />
      <rect x="4" y="10" width="1" height="2" fill={DARK} />
      <rect x="11" y="10" width="1" height="2" fill={DARK} />
      <rect x="5" y="12" width="1" height="1" fill={DARK} />
      <rect x="10" y="12" width="1" height="1" fill={DARK} />
      <rect x="6" y="13" width="1" height="1" fill={DARK} />
      <rect x="9" y="13" width="1" height="1" fill={DARK} />
      <rect x="7" y="14" width="2" height="1" fill={DARK} />
    </svg>
  );
}

/** Check mark. */
export function CheckSprite({ size = 16, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="3" y="8" width="2" height="2" fill={DARK} />
      <rect x="5" y="10" width="2" height="2" fill={DARK} />
      <rect x="7" y="8" width="2" height="2" fill={DARK} />
      <rect x="9" y="6" width="2" height="2" fill={DARK} />
      <rect x="11" y="4" width="2" height="2" fill={DARK} />
      <rect x="9" y="2" width="2" height="2" fill={DARK} />
    </svg>
  );
}

/** X / close. */
export function XSprite({ size = 16, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="2" y="2" width="2" height="2" fill={DARK} />
      <rect x="4" y="4" width="2" height="2" fill={DARK} />
      <rect x="6" y="6" width="2" height="2" fill={DARK} />
      <rect x="8" y="6" width="2" height="2" fill={DARK} />
      <rect x="10" y="4" width="2" height="2" fill={DARK} />
      <rect x="12" y="2" width="2" height="2" fill={DARK} />
      <rect x="2" y="12" width="2" height="2" fill={DARK} />
      <rect x="4" y="10" width="2" height="2" fill={DARK} />
      <rect x="10" y="10" width="2" height="2" fill={DARK} />
      <rect x="12" y="12" width="2" height="2" fill={DARK} />
    </svg>
  );
}

/** Pixel arrow. */
export function ArrowSprite({ size = 16, dir = 'right', style, className }: SpriteProps & { dir?: 'right' | 'down' | 'left' | 'up' }) {
  if (dir === 'right') {
    return (
      <svg {...baseProps(size)} style={style} className={className}>
        <rect x="2" y="7" width="10" height="2" fill={DARK} />
        <rect x="10" y="5" width="2" height="2" fill={DARK} />
        <rect x="12" y="3" width="2" height="2" fill={DARK} />
        <rect x="10" y="9" width="2" height="2" fill={DARK} />
        <rect x="12" y="11" width="2" height="2" fill={DARK} />
      </svg>
    );
  }
  if (dir === 'down') {
    return (
      <svg {...baseProps(size)} style={style} className={className}>
        <rect x="7" y="2" width="2" height="10" fill={DARK} />
        <rect x="5" y="10" width="2" height="2" fill={DARK} />
        <rect x="3" y="12" width="2" height="2" fill={DARK} />
        <rect x="9" y="10" width="2" height="2" fill={DARK} />
        <rect x="11" y="12" width="2" height="2" fill={DARK} />
      </svg>
    );
  }
  if (dir === 'left') {
    return (
      <svg {...baseProps(size)} style={style} className={className}>
        <rect x="4" y="7" width="10" height="2" fill={DARK} />
        <rect x="4" y="5" width="2" height="2" fill={DARK} />
        <rect x="2" y="3" width="2" height="2" fill={DARK} />
        <rect x="4" y="9" width="2" height="2" fill={DARK} />
        <rect x="2" y="11" width="2" height="2" fill={DARK} />
      </svg>
    );
  }
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="7" y="4" width="2" height="10" fill={DARK} />
      <rect x="5" y="4" width="2" height="2" fill={DARK} />
      <rect x="3" y="2" width="2" height="2" fill={DARK} />
      <rect x="9" y="4" width="2" height="2" fill={DARK} />
      <rect x="11" y="2" width="2" height="2" fill={DARK} />
    </svg>
  );
}

/** Pixel "key" — for auth. */
export function KeySprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="2" y="6" width="4" height="4" fill={DARK} />
      <rect x="3" y="7" width="2" height="2" fill={SHADOW} />
      <rect x="6" y="7" width="8" height="2" fill={DARK} />
      <rect x="12" y="5" width="2" height="6" fill={DARK} />
      <rect x="14" y="7" width="2" height="2" fill={DARK} />
    </svg>
  );
}

/** Mail envelope — for magic link. */
export function MailSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="2" y="4" width="12" height="8" fill={DARK} />
      <rect x="3" y="5" width="10" height="6" fill={SHADOW} />
      <rect x="3" y="5" width="10" height="1" fill={DARK} />
      {/* envelope flap */}
      <rect x="3" y="6" width="1" height="1" fill={DARK} />
      <rect x="12" y="6" width="1" height="1" fill={DARK} />
      <rect x="4" y="7" width="1" height="1" fill={DARK} />
      <rect x="11" y="7" width="1" height="1" fill={DARK} />
      <rect x="5" y="8" width="1" height="1" fill={DARK} />
      <rect x="10" y="8" width="1" height="1" fill={DARK} />
      <rect x="6" y="9" width="1" height="1" fill={DARK} />
      <rect x="9" y="9" width="1" height="1" fill={DARK} />
      <rect x="7" y="10" width="2" height="1" fill={DARK} />
    </svg>
  );
}

/** Microphone — for the voicemails screen. */
export function MicSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      {/* mic body */}
      <rect x="6" y="2" width="4" height="2" fill={DARK} />
      <rect x="5" y="4" width="6" height="6" fill={DARK} />
      <rect x="6" y="5" width="4" height="4" fill={SHADOW} />
      {/* mic stem */}
      <rect x="7" y="10" width="2" height="2" fill={DARK} />
      {/* base */}
      <rect x="4" y="12" width="8" height="1" fill={DARK} />
      <rect x="5" y="13" width="6" height="1" fill={DARK} />
      {/* sound waves */}
      <rect x="2" y="6" width="1" height="2" fill={DARK} />
      <rect x="13" y="6" width="1" height="2" fill={DARK} />
    </svg>
  );
}

/** Play triangle — for the play button on voicemails. */
export function PlaySprite({ size = 16, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="4" y="3" width="2" height="2" fill={DARK} />
      <rect x="4" y="5" width="2" height="2" fill={DARK} />
      <rect x="4" y="7" width="2" height="2" fill={DARK} />
      <rect x="4" y="9" width="2" height="2" fill={DARK} />
      <rect x="4" y="11" width="2" height="2" fill={DARK} />
      <rect x="6" y="4" width="2" height="2" fill={DARK} />
      <rect x="6" y="6" width="2" height="2" fill={DARK} />
      <rect x="6" y="8" width="2" height="2" fill={DARK} />
      <rect x="6" y="10" width="2" height="2" fill={DARK} />
      <rect x="8" y="5" width="2" height="2" fill={DARK} />
      <rect x="8" y="7" width="2" height="2" fill={DARK} />
      <rect x="8" y="9" width="2" height="2" fill={DARK} />
      <rect x="10" y="6" width="2" height="2" fill={DARK} />
      <rect x="10" y="8" width="2" height="2" fill={DARK} />
      <rect x="12" y="7" width="2" height="2" fill={DARK} />
    </svg>
  );
}

/** Pause — used when a voicemail is playing. */
export function PauseSprite({ size = 16, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="4" y="3" width="2" height="10" fill={DARK} />
      <rect x="6" y="3" width="2" height="10" fill={DARK} />
      <rect x="8" y="3" width="2" height="10" fill={DARK} />
      <rect x="10" y="3" width="2" height="10" fill={DARK} />
    </svg>
  );
}

/** Crown sprite — for presidente/admin role. */
export function CrownSprite({ size = 24, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      {/* Crown base bar */}
      <rect x="3" y="10" width="10" height="2" fill={DARK} />
      {/* Crown body */}
      <rect x="3" y="6" width="10" height="4" fill={DARK} />
      {/* Crown peaks (left, center, right) */}
      <rect x="3" y="4" width="2" height="2" fill={DARK} />
      <rect x="7" y="2" width="2" height="4" fill={DARK} />
      <rect x="11" y="4" width="2" height="2" fill={DARK} />
      {/* Gems on the base */}
      <rect x="5" y="8" width="1" height="1" fill="#b30f42" />
      <rect x="10" y="8" width="1" height="1" fill="#b30f42" />
      {/* Crown tip dots (jewels) */}
      <rect x="3" y="3" width="1" height="1" fill="#b30f42" />
      <rect x="8" y="1" width="1" height="1" fill="#b30f42" />
      <rect x="12" y="3" width="1" height="1" fill="#b30f42" />
    </svg>
  );
}

/** Loading dots. */
export function LoadingSprite({ size = 16, style, className }: SpriteProps) {
  return (
    <svg {...baseProps(size)} style={style} className={className}>
      <rect x="2" y="6" width="3" height="3" fill={DARK}>
        <animate attributeName="opacity" values="0.3;1;0.3" dur="1s" begin="0s" repeatCount="indefinite" />
      </rect>
      <rect x="6" y="6" width="3" height="3" fill={DARK}>
        <animate attributeName="opacity" values="0.3;1;0.3" dur="1s" begin="0.2s" repeatCount="indefinite" />
      </rect>
      <rect x="10" y="6" width="3" height="3" fill={DARK}>
        <animate attributeName="opacity" values="0.3;1;0.3" dur="1s" begin="0.4s" repeatCount="indefinite" />
      </rect>
    </svg>
  );
}
